import { autoinject, bindable, computedFrom } from 'aurelia-framework';
import { BaseDevice } from "./base-device";
import { Store } from "aurelia-store";
import * as State from "../../../store/state";
import { applicationLogger } from "../../../logging/logger";
import IState = State.IState;
import { AsEnumerable } from "linq-es2015/lib/linq";
import { Api } from "../../../services/api";
import { Device } from "../../../models/device";
import { I18N } from "aurelia-i18n";
import * as moment from 'moment';

@autoinject
export class Dryer extends BaseDevice {
	i18n: I18N;
	disabled: boolean = false;

	private bottomNutsMoistureInRangeCss_: string;
	private blowerOnCss_: string;
	private uniqueId: string;


	constructor(store: Store<IState>, api: Api, i18n: I18N) {
		super(store, api);
		this.i18n = i18n;
		this.uniqueId = this.generateUniqueId();
	}


	public topTemperatureHumidity: Device;
	public middleTemperatureHumidity: Device;
	public bottomTemperatureHumidity: Device;
	public moisture: Device;
	public topMoisture: Device;

	activate(device: Device) {
		this.device = device;

		if (this.device.children.length > 0) {
			this.topTemperatureHumidity = this.getChildDeviceByPosition("top");
			this.middleTemperatureHumidity = this.getChildDeviceByPosition("middle");
			this.bottomTemperatureHumidity = this.getChildDeviceByPosition("bottom");
			this.moisture = this.getChildDeviceByPosition("moisture");
			this.topMoisture = this.getChildDeviceByPosition("top-moisture");
		}
	}

	private generateUniqueId(): string {
		return Math.random().toString(36).slice(2, 11) + '-' + Date.now();
	}

	@computedFrom('topTemperatureHumidity.data', 'middleTemperatureHumidity.data', 'bottomTemperatureHumidity.data', 'moisture.data', 'topMoisture.data')
	get latestTimestamp(): Date | null {
		const timestamps = [
			this.topTemperatureHumidity?.data?.timestamp ? moment(this.topTemperatureHumidity.data.timestamp) : null,
			this.middleTemperatureHumidity?.data?.timestamp ? moment(this.middleTemperatureHumidity.data.timestamp) : null,
			this.bottomTemperatureHumidity?.data?.timestamp ? moment(this.bottomTemperatureHumidity.data.timestamp) : null,
			this.moisture?.data?.timestamp ? moment(this.moisture.data.timestamp) : null,
			this.topMoisture?.data?.timestamp ? moment(this.topMoisture.data.timestamp) : null
		].filter(t => t !== null); // Remove null values

		return timestamps.length > 0 ? moment.max(...timestamps).toDate() : null;
	}


	getChildDeviceByPosition(position: string): Device {
		return this.device.children.find(
			child => child.deviceProperties.find(
				property => property.name === "position" && property.value === position));
	}

	unbind() {
		this.unsubscribe();
	}

	attached() {
		this.blowerOnCss = "blower-off";
		//this.bottomNutsMoistureInRangeCss = "s-moisture-in-range";
	}

	@computedFrom('moisture')
	get hasMoistureSensor() {
		return this.isDefined(this.moisture);
	}

	@computedFrom('topMoisture')
	get hasTopMoistureSensor() {
		return this.isDefined(this.topMoisture);
	}

	@computedFrom('topMoisture.data')
	get showTopMoisture(): boolean {
		return this.topAverageNutMoisture != null; // && this.topAverageNutMoisture > 3;
	}

	@computedFrom('topMoisture.data')
	get topAverageNutMoisture(): number {
		if (this.topMoisture === undefined || this.topMoisture === null || this.topMoisture.data === undefined || this.topMoisture.data == null) {
			return null;
		}

		var data = this.topMoisture.data;
		var measureCount = 0;
		var moisture = 0.0;

		if (data.moisture1 != null && data.moisture1 != 0.0) {
			moisture += data.moisture1;
			measureCount++;
		}
		if (data.moisture2 != null && data.moisture2 != 0.0) {
			moisture += data.moisture2;
			measureCount++;
		}
		if (data.moisture3 != null && data.moisture3 != 0.0) {
			moisture += data.moisture3;
			measureCount++;
		}
		if (data.moisture4 != null && data.moisture4 != 0.0) {
			moisture += data.moisture4;
			measureCount++;
		}
		if (data.moisture5 != null && data.moisture5 != 0.0) {
			moisture += data.moisture5;
			measureCount++;
		}
		if (data.moisture6 != null && data.moisture6 != 0.0) {
			moisture += data.moisture6;
			measureCount++;
		}
		if (data.moisture7 != null && data.moisture7 != 0.0) {
			moisture += data.moisture7;
			measureCount++;
		}
		if (data.moisture8 != null && data.moisture8 != 0.0) {
			moisture += data.moisture8;
			measureCount++;
		}

		return measureCount > 0 ? moisture / measureCount : null;
	}

	@computedFrom('topMoisture.data')
	get topMoistureDisplayValue(): number {
		if (this.showTopMoisture && this.topAverageNutMoisture != null) {
			return this.topAverageNutMoisture;
		}

		return null;
	}

	@computedFrom('topMoisture.data')
	get topMoistureTemperatureDisplayValue(): number {
		if (this.topAverageNutTemperature != null) {
			return this.topAverageNutTemperature;
		}

		return null;
	}

	@computedFrom('topMoisture.data')
	get topAverageNutTemperature(): number {
		return this.topMoisture != null && this.topMoisture.data != null ? (this.topMoisture.data.temperature1 + this.topMoisture.data.temperature2 + this.topMoisture.data.temperature3 + this.topMoisture.data.temperature4 + this.topMoisture.data.temperature5 + this.topMoisture.data.temperature6) / 6 : null;
	}

	@computedFrom('topTemperatureHumidity')
	get hasTopTemperatureHumiditySensor() {
		return this.isDefined(this.topTemperatureHumidity);
	}

	@computedFrom('middleTemperatureHumidity')
	get hasMiddleTemperatureHumiditySensor() {
		return this.isDefined(this.middleTemperatureHumidity);
	}

	@computedFrom('bottomTemperatureHumidity')
	get hasBottomTemperatureHumiditySensor() {
		return this.isDefined(this.bottomTemperatureHumidity);
	}

	@computedFrom('moisture.data')
	get showMoisture(): boolean {
		return this.moisture != null && this.moisture.data != null && this.moisture.data.lotIsInLowerBin != null ? this.moisture.data.lotIsInLowerBin : false;
	}

	get emptyMessage(): string {
		return this.i18n.tr("empty");
	}

	get offlineMessage(): string {
		return this.i18n.tr("offline");
	}

	@computedFrom('moisture.data')
	get bottomNutsMoistureInRangeCss() {

		var style = this.moisture != null && this.moisture.styles != null ? this.moisture.styles["averageMoisture"] : null;

		style = style ? style : "";
		return style;
	}

	//set bottomNutsMoistureInRangeCss(value: string) {
	//	this.bottomNutsMoistureInRangeCss_ = value;
	//}


	@computedFrom('moisture.data')
	get blowerOnCss() {

		//if (this.moisture && this.moisture.data) {
		//	if (this.moisture.data.digitalInput1 === 0 && this.moisture.data.digitalInput2 === 0) {
		//		this.bottomNutsMoistureInRangeCss = "bottom-nuts-moisture-in-range";
		//	}

		//}

		this.blowerOnCss_ = "blower-off";

		return this.blowerOnCss_;
	}

	set blowerOnCss(value: string) {
		this.bottomNutsMoistureInRangeCss_ = value;
	}

	@computedFrom('moisture.data')
	get averageNutMoisture(): number {
		return this.moisture != null && this.moisture.data != null && this.moisture.data.averageMoisture != null ? this.moisture.data.averageMoisture : null;
		//return this.calculateAverageNutMoisture();
	}

	@computedFrom('moisture.data')
	get averageNutTemperature(): number {
		return this.moisture != null && this.moisture.data != null ? (this.moisture.data.temperature1 + this.moisture.data.temperature2 + this.moisture.data.temperature3 + this.moisture.data.temperature4 + this.moisture.data.temperature5 + this.moisture.data.temperature6) / 6 : null;
	}

	@computedFrom('moisture.data')
	get variety(): string {
		if (this.moisture === undefined || this.moisture === null || this.moisture.data === undefined || this.moisture.data == null) {
			return "v: unknown";
		}

		return this.moisture.data.variety !== null ? "v: " + this.moisture.data.variety : "v: unknown";
	}

	@computedFrom('moisture.data')
	get batchWeight(): string {

		if (this.moisture === undefined || this.moisture === null || this.moisture.data === undefined || this.moisture.data == null) {
			return "w: unknown";
		}
		return this.moisture.data.batchWeight ? "w: " + this.moisture.data.batchWeight.toLocaleString('en-US') + " lbs" : "w: unknown";
	}

	@computedFrom('moisture.data')
	get moistureAdjustment(): string {


		if (this.moisture === undefined || this.moisture === null || this.moisture.data === undefined || this.moisture.data == null) {
			return "ma: unknown";
		}

		if (this.moisture.data.moistureAdjustment != null) {
			var adjustment = this.moisture.data.moistureAdjustment as number;
			return "ma: " + adjustment.toFixed(1); // Ensure one decimal place
		}
		return "ma: unknown";
	}


	@computedFrom('moisture.data')
	get maximumSoilMoisture(): number {
		return this.findMaximumNutMoisture();
	}

	@computedFrom('moisture.data')
	get medianSoilMoisture(): number {
		return this.findMedianNutMoisture();
	}

	findMedianNutMoisture(): number {


		if (this.moisture === undefined || this.moisture === null || this.moisture.data === undefined || this.moisture.data == null) {
			return null;
		}

		var data = this.moisture.data;
		const numbers: number[] = [];

		if (data.moisture1 != 0.0) {
			numbers.push(data.moisture1);
		};

		if (data.moisture2 != 0.0) {
			numbers.push(data.moisture2);
		}

		if (data.moisture3 != 0.0) {
			numbers.push(data.soilMoisture3);
		}

		if (data.moisture4 != 0.0) {
			numbers.push(data.soilMoisture4);
		};

		if (data.moisture5 != 0.0) {
			numbers.push(data.soilMoisture5);
		};

		if (data.moisture6 != 0.0) {
			numbers.push(data.soilMoisture6);
		};

		if (data.moisture7 != 0.0) {
			numbers.push(data.soilMoisture7);
		};

		if (data.moisture8 != 0.0) {
			numbers.push(data.soilMoisture8);
		};

		// Sort the array in ascending order
		numbers.sort((a, b) => a - b);

		const length = numbers.length;
		const middle = Math.floor(length / 2);

		// Check if the array length is even or odd
		if (length % 2 === 0) {
			// If even, return the average of the two middle elements
			return (numbers[middle - 1] + numbers[middle]) / 2;
		} else {
			// If odd, return the middle element
			return numbers[middle];
		}
	}

	private findMaximumNutMoisture(): number {


		if (this.moisture === undefined || this.moisture === null || this.moisture.data === undefined || this.moisture.data == null) {
			return null;
		}

		var data = this.moisture.data;
		const numbers: number[] = [data.moisture1, data.moisture2, data.moisture3, data.moisture4, data.moisture5, data.moisture6, data.moisture7, data.moisture8];
		const max: number = Math.max(...numbers);

		return max;
	}

	private calculateAverageNutMoisture(): number {
		if (this.moisture === undefined || this.moisture === null || this.moisture.data === undefined || this.moisture.data == null) {
			return null;
		}

		var data = this.moisture.data;
		var measureCount = 0;
		var moisture = 0.0;

		if (data.moisture1 != null && data.moisture1 != 0.0) {
			moisture += data.moisture1;
			measureCount++;
		}

		if (data.moisture2 != null && data.moisture2 != 0.0) {
			moisture += data.moisture2;
			measureCount++;
		}
		if (data.moisture3 != null && data.moisture3 != 0.0) {
			moisture += data.moisture3;
			measureCount++;
		}
		if (data.moisture4 != null && data.moisture4 != 0.0) {
			moisture += data.moisture4;
			measureCount++;
		}
		if (data.moisture5 != null && data.moisture5 != 0.0) {
			moisture += data.moisture5;
			measureCount++;
		}
		if (data.moisture6 != null && data.moisture6 != 0.0) {
			moisture += data.moisture6;
			measureCount++;
		}

		if (data.moisture7 != null && data.moisture7 != 0.0) {
			moisture += data.moisture7;
			measureCount++;
		}
		if (data.moisture8 != null && data.moisture8 != 0.0) {
			moisture += data.moisture6;
			measureCount++;
		}


		return measureCount > 0 ? moisture / measureCount : moisture;
	}
}